import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { AmenityLocationSection } from "../components/AmenityLocationSection";
import { SiteRoadwaysSection } from "../components/SiteRoadwaysSection";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DashboardHeader from "@components/Header/DashboardHeader";
import GenericBreadCrums from "@components/Breadcrumbs";
import { UploadedDocuments } from "../components/UploadedDocuments";
import { Consent } from "../components/Consent";
import { IndividualDetailsPreview } from "../components/IndividualDetails";
import { useNavigate } from "react-router-dom";
import { ApplicantOrganisationDetails } from "../components/ApplicantOrganisationDetails";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootReducerType, RootState } from "@store/store";
import { fetchApplicationPreview } from "./redux/thunk/PreviewThunk";
// import bgImage from "../../../assets/images/BG.svg";
import { setActiveStep } from "../../stepper/redux/stepperSlice";
import { MediaUpload } from "../components/MediaUpload";
import ScrollToTopButton from "../../../../src/utils/scrollToTopButton";
import { resetApplicationSubmitted } from "../application-details/redux/applicationSubmittedSlice";

export const ApplicationPreview: React.FC = () => {
  const breadcrumbData = [{ label: "Application Details", link: "#" }];
  const [isApplicationPreviewExpanded, setIsApplicationPreviewExpanded] =
    useState({
      individualDetails: true,
      organisationDetails: false,
      ammentiesDetails: false,
      siteRoadways: false,
      documents: false,
      consent: false,
      uploadMedia: false,
    });
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [consentCheck, setConsentCheck] = useState(false);

  const applicationPreview = useSelector(
    (state: RootState) => state.applicationPreview?.applicationPreview
  );

  const { access_id } = useSelector(
    (state: RootState) => state.amenities || {}
  );
  const isApplicationSubmitted = useSelector(
    (state: RootReducerType) =>
      state.isApplicationSubmitted?.isApplicationSubmitted
  );

  const handleAgree = () => {
    setShowDisclaimerModal(false);
    dispatch(setActiveStep(4));
    dispatch(resetApplicationSubmitted());
    navigate("/stepper");
  };

  useEffect(() => {
    if (access_id) {
      dispatch(fetchApplicationPreview(access_id));
      // Show disclaimer modal when page loads
      // setShowDisclaimerModal(true);
    }
  }, [access_id, dispatch]);

  React.useEffect(() => {
    if (isApplicationSubmitted) {
      navigate(`/application-details`, { replace: true });
    }
  }, []);

  if (!access_id || !applicationPreview) return null;

  const allCollapsed = Object.values(isApplicationPreviewExpanded).every(
    (value) => value === false
  );

  if (!applicationPreview) return;

  const toggleApplicationPreviewSections = (expanded: boolean) => {
    setIsApplicationPreviewExpanded({
      individualDetails: expanded,
      organisationDetails: expanded,
      ammentiesDetails: expanded,
      siteRoadways: expanded,
      documents: expanded,
      consent: expanded,
      uploadMedia: expanded,
    });
  };

  return (
    <Box
      className="login-root"
      sx={{
        // background: `url(${bgImage}) no-repeat top center`,
        backgroundSize: "cover", // or "contain" depending on your layout
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Disclaimer Modal */}
      <Dialog
        open={showDisclaimerModal}
        onClose={() => setShowDisclaimerModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            pr: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon sx={{ color: "#ff9800", fontSize: 32 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#3a1c73" }}>
              Instructions
            </Typography>
          </Box>
          <IconButton
            onClick={() => setShowDisclaimerModal(false)}
            sx={{ color: "#666" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            {/* <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                fontWeight: 600,
                color: "#000",
                mb: 2,
              }}
            >
              Important Notice:
            </Typography> */}
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                color: "#333",
                lineHeight: 1.7,
                mb: 2,
              }}
            >
              Before proceeding with the application submission, please review
              and verify all the information provided. All submitted details
              will be verified by the Authority. If any incorrect, incomplete,
              or misleading information is found, the application shall be
              cancelled.
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: "#fff3cd",
              border: "1px solid #ffc107",
              borderRadius: 1,
              padding: 2.5,
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.95rem",
                color: "#856404",
                fontWeight: 600,
                mb: 1.5,
              }}
            >
              Please ensure the following:
            </Typography>
            <Box component="ul" sx={{ margin: 0, paddingLeft: 2.5 }}>
              <Typography component="li" sx={{ mb: 1, color: "#856404" }}>
                All personal and organization details are accurate and
                up-to-date.
              </Typography>
              <Typography component="li" sx={{ mb: 1, color: "#856404" }}>
                Site location and amenity information is correct and complete.
              </Typography>
              <Typography component="li" sx={{ mb: 1, color: "#856404" }}>
                All required documents have been uploaded and are clearly
                legible.
              </Typography>
              <Typography component="li" sx={{ mb: 1, color: "#856404" }}>
                Measurements, dimensions, and technical specifications are
                precise.
              </Typography>
              {/* <Typography component="li" sx={{ mb: 0, color: "#856404" }}>
                All consent declarations and acknowledgments are truthful
              </Typography> */}
            </Box>
          </Box>

          {/* <Box
            sx={{
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c6cb",
              borderRadius: 1,
              padding: 2,
              mb: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.95rem",
                color: "#721c24",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 20, color: "#dc3545" }} />
              Warning:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.9rem",
                color: "#721c24",
                mt: 1,
                lineHeight: 1.6,
              }}
            >
              Providing incorrect, incomplete, or misleading information may result in:
            </Typography>
            <Box component="ul" sx={{ margin: "8px 0 0 0", paddingLeft: 2.5 }}>
              <Typography component="li" sx={{ mb: 0.5, color: "#721c24", fontSize: "0.9rem" }}>
                Immediate rejection of your application
              </Typography>
              <Typography component="li" sx={{ mb: 0.5, color: "#721c24", fontSize: "0.9rem" }}>
                Delays in processing and approval
              </Typography>
              <Typography component="li" sx={{ mb: 0, color: "#721c24", fontSize: "0.9rem" }}>
                Potential legal consequences or penalties
              </Typography>
            </Box>
          </Box> */}

          {/* <Typography
            variant="body2"
            sx={{
              fontSize: "0.9rem",
              color: "#555",
              fontStyle: "italic",
              mt: 2,
              textAlign: "center",
            }}
          >
            Take your time to review each section thoroughly. You can expand all sections using the "Expand All" button above.
          </Typography> */}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            variant="contained"
            className="stepper-next-btn"
            sx={{ width: "auto" }}
            onClick={() => handleAgree()}
          >
            I Agree
          </Button>
        </DialogActions>
      </Dialog>

      <div className="page-content">
        {/* <DashboardHeader /> */}
        <GenericBreadCrums breadcrumbs={breadcrumbData} className="mt-6" />
        <Box className="preview-wrapper">
          <Box className="preview-header">
            <Box className="preview-title-box">
              <Typography className="preview-title">
                Application Preview
              </Typography>
            </Box>
            <Box className="preview-controls">
              {allCollapsed ? (
                <Button
                  startIcon={<UnfoldMoreIcon className="rotate-45 brand-900" />}
                  onClick={() => toggleApplicationPreviewSections(true)}
                >
                  <Typography
                    className="brand-900"
                    sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                  >
                    Expand All
                  </Typography>
                </Button>
              ) : (
                <Button
                  startIcon={<UnfoldLessIcon className="rotate-45 brand-900" />}
                  onClick={() => toggleApplicationPreviewSections(false)}
                >
                  <Typography
                    className="brand-900"
                    sx={{ whiteSpace: "nowrap", fontWeight: 700 }}
                  >
                    Collapse All
                  </Typography>
                </Button>
              )}
            </Box>
          </Box>

          <Box className="preview-sections flex flex-col gap-6">
            <IndividualDetailsPreview
              isExpanded={isApplicationPreviewExpanded.individualDetails}
              toggleExpand={() =>
                setIsApplicationPreviewExpanded((prev) => ({
                  ...prev,
                  individualDetails: !prev.individualDetails,
                }))
              }
              data={applicationPreview}
            />
            <ApplicantOrganisationDetails
              isExpanded={isApplicationPreviewExpanded.organisationDetails}
              toggleExpand={() =>
                setIsApplicationPreviewExpanded((prev) => ({
                  ...prev,
                  organisationDetails: !prev.organisationDetails,
                }))
              }
              data={applicationPreview}
            />
            <AmenityLocationSection
              isExpanded={isApplicationPreviewExpanded.ammentiesDetails}
              toggleExpand={() =>
                setIsApplicationPreviewExpanded((prev) => ({
                  ...prev,
                  ammentiesDetails: !prev.ammentiesDetails,
                }))
              }
              data={applicationPreview}
            />

            <SiteRoadwaysSection
              isExpanded={isApplicationPreviewExpanded.siteRoadways}
              toggleExpand={() =>
                setIsApplicationPreviewExpanded((prev) => ({
                  ...prev,
                  siteRoadways: !prev.siteRoadways,
                }))
              }
              data={applicationPreview}
            />
            <UploadedDocuments
              isExpanded={isApplicationPreviewExpanded.documents}
              toggleExpand={() =>
                setIsApplicationPreviewExpanded((prev) => ({
                  ...prev,
                  documents: !prev.documents,
                }))
              }
            />
            <MediaUpload
              isExpanded={isApplicationPreviewExpanded.uploadMedia}
              toggleExpand={() =>
                setIsApplicationPreviewExpanded((prev) => ({
                  ...prev,
                  uploadMedia: !prev.uploadMedia,
                }))
              }
            />
            {/* <Consent
              isExpanded={isApplicationPreviewExpanded.consent}
              toggleExpand={() =>
                setIsApplicationPreviewExpanded((prev) => ({
                  ...prev,
                  consent: !prev.consent,
                }))
              }
              setConsentCheck={setConsentCheck}
              consentCheck={consentCheck}
            /> */}
          </Box>
          <Box
            className="edit-box flex justify-end gap-3 align-center gray:br-t-500 pt-4"
            mt={2}
          >
            <Button
              variant="outlined"
              className="back-btn"
              onClick={() => {
                dispatch(resetApplicationSubmitted());
                navigate("/stepper");
                dispatch(setActiveStep(2));
              }}
            >
              <Typography textTransform="initial">Back</Typography>
            </Button>
            <Button
              variant="contained"
              className="stepper-next-btn"
              sx={{ width: "auto" }}
              // disabled={!consentCheck}
              // sx={{
              //   opacity: consentCheck ? 1 : 0.5,
              //   pointerEvents: consentCheck ? "auto" : "none",
              // }}
              onClick={() => {
                setShowDisclaimerModal(true);
                // dispatch(setActiveStep(4));
                // navigate("/stepper");
              }}
            >
              <Typography textTransform="initial">Next</Typography>
            </Button>
          </Box>
          <ScrollToTopButton />
        </Box>
      </div>
    </Box>
  );
};
